import React, { useState } from "react";
import { PaziaModal } from "components/molecules";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { ModalHeader } from "../../components/templates/Modals/ModalHeader";
import { ModalPadding } from "../../components/templates/Modals/ModalPadding";
import { useCourseGroupAttributesAll } from "api/coursegroup-attributes/useCourseGroupAttributes";
import { coursegroupAttributesApi } from "api/coursegroup-attributes/coursegroupAttributesApi";
import { useToaster } from "utils/toaster";

interface BulkUnassignAttributesModalProps {
  isOpen: boolean;
  onCloseRequest: () => void;
  selectedCourseGroupIds: number[];
  onSuccess: () => void;
}

export const BulkUnassignAttributesModal = ({
  isOpen,
  onCloseRequest,
  selectedCourseGroupIds,
  onSuccess,
}: BulkUnassignAttributesModalProps) => {
  const { isLoading: loadingAttributes, allAttributes } = useCourseGroupAttributesAll();
  const [selectedAttributeIds, setSelectedAttributeIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useToaster();

  const toggleAttribute = (id: number) => {
    setSelectedAttributeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await coursegroupAttributesApi.bulkUnassign({
        courseGroupIds: selectedCourseGroupIds,
        courseGroupAttributeIds: Array.from(selectedAttributeIds),
      });
      showSuccess("Odlaczono atrybuty");
      onSuccess();
      handleClose();
    } catch {
      showError("Nie udalo sie odlaczyc atrybutow");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedAttributeIds(new Set());
    onCloseRequest();
  };

  return (
    <PaziaModal id="bulk-unassign-attributes-modal" isOpen={isOpen} onCloseRequest={handleClose} size="lg">
      <ModalPadding>
        <ModalHeader>Odlacz atrybuty ({selectedCourseGroupIds.length} grup)</ModalHeader>
        {loadingAttributes ? (
          <PaziaLoader position="center" />
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {allAttributes.map((attr) => (
              <label key={attr.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 rounded">
                <input
                  type="checkbox"
                  checked={selectedAttributeIds.has(attr.id)}
                  onChange={() => toggleAttribute(attr.id)}
                  className="form-checkbox"
                />
                <span>{attr.name}</span>
              </label>
            ))}
            {allAttributes.length === 0 && (
              <p className="text-slate-500 text-sm">Brak dostepnych atrybutow</p>
            )}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <PaziaButton type="secondary" onClick={handleClose} disabled={saving}>
            Anuluj
          </PaziaButton>
          <PaziaButton
            type="danger"
            onClick={handleSubmit}
            disabled={selectedAttributeIds.size === 0 || saving}
            processing={saving}
          >
            Odlacz
          </PaziaButton>
        </div>
      </ModalPadding>
    </PaziaModal>
  );
};
